import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  MessageSquare,
  Send,
  Layers,
  Users,
  Maximize2,
  Sliders,
  Zap,
  Film,
  Cloud,
  Coffee,
  Link2,
  PictureInPicture2,
  Wand2,
  Radio as RadioIcon,
  X,
  Music2,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import AdvancedDirectorPanel from "../components/AdvancedDirectorPanel";
import DirectorMixerRack from "../components/DirectorMixerRack";
import StudioFxModal from "../components/StudioFxModal";
import GuestManager from "../components/GuestManager";
import NewsroomControls from "../components/NewsroomControls";
import MediaSourcePicker from "../components/MediaSourcePicker";
import AdSchedule from "../components/AdSchedule";
import CameraSettingsPanel, { DEFAULT_CAM_SETTINGS } from "../components/CameraSettingsPanel";
import RestreamPanel from "../components/dj/RestreamPanel";
import AnnouncementsPanel from "../components/dj/AnnouncementsPanel";
import PhoneCameraPanel from "../components/dj/PhoneCameraPanel";
// Feature parity with DirectorsMode — bring the social share bar, AI
// thumbnail picker, and QR overlay panel/layer into Full Studio so hosts
// aren't forced to leave to grab those tools.
import GoLiveShareBar from "../components/GoLiveShareBar";
import AiThumbnailPicker from "../components/AiThumbnailPicker";
import QrOverlayPanel, { QrOverlayLayer } from "../components/QrOverlayPanel";
import ConsoleStagePanel from "../components/director/ConsoleStagePanel";
import ConsoleTopToolbar from "../components/director/ConsoleTopToolbar";
import ConsoleScenesRail from "../components/director/ConsoleScenesRail";
import ConsoleAnnouncerFx from "../components/director/ConsoleAnnouncerFx";
import {
  MonitorTile,
  BackstagePanel,
  CameraPicker,
} from "../components/director/ConsoleParts";

/**
 * DirectorConsole — full-screen broadcast console.
 *
 * This page WRAPS the existing `AdvancedDirectorPanel` in an immersive
 * full-viewport layout inspired by vMix (dark console, channel faders) and
 * Switcher Studio (big program preview, tucked-away chat).
 *
 * Every feature already shipped on `/studio/live` (soundboard, cam/mic
 * toggles, scene switcher, overlays, WebRTC broadcaster, studio mixer strip,
 * announcer FX, PIP, Weather, BRB, TV Guide) is preserved — this page just
 * re-frames the panel with:
 *
 *  - A top console toolbar (Exit, title, invite code, On-Air pill, viewers,
 *    Comments toggle, Chat drawer toggle)
 *  - A left "Sources" rail with scene presets (legacy simulated scenes) so the
 *    user can switch scenes without scrolling
 *  - A right-side slide-out chat drawer (hidden by default — "hide comments
 *    neatly" per the user ask). When open, it fetches the stream's chat every
 *    4s and lets the director send messages.
 *  - The AdvancedDirectorPanel (with its StudioMixerStrip + multi-cam +
 *    soundboard + overlays) in the center, sized to fit whatever the rails
 *    leave free.
 *
 * All routes/state/endpoints used by DirectorsMode stay untouched — the
 * legacy page at /studio/live still works and is actually the only entry-
 * point from which the user can reach this console.
 */
const SCENES = [
  { id: "camera", label: "Main Camera" },
  { id: "intro", label: "Intro Clip" },
  { id: "guest", label: "Guest Cam" },
  { id: "ad", label: "Ad Break" },
  { id: "brb", label: "Be Right Back" },
  { id: "outro", label: "Outro" },
];

export default function DirectorConsole() {
  const { streamId } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();

  const [stream, setStream] = useState(null);
  const [loading, setLoading] = useState(true);
  const [chatOpen, setChatOpen] = useState(false);
  const [mixerOpen, setMixerOpen] = useState(true);
  const [mixerFloat, setMixerFloat] = useState(false);
  const [proKitOpen, setProKitOpen] = useState(false);
  const [soundboardPresets, setSoundboardPresets] = useState([]);
  const [bcStatus, setBcStatus] = useState("idle");
  const [audioBus, setAudioBus] = useState({ audioCtx: null, outputDest: null });
  const [fxOpen, setFxOpen] = useState(false);
  // Mobile single-panel focus — 'scenes' | 'preview' | 'mixer' | 'guests' | 'overlays'
  // Above md, all 3 columns show simultaneously (plus mixer docked under preview).
  const [mobilePanel, setMobilePanel] = useState("preview");
  const [fxSettings, setFxSettings] = useState(() => {
    try { return JSON.parse(localStorage.getItem("tapin_studio_fx") || "null") || null; } catch { return null; }
  });
  const [guests, setGuests] = useState([]); // {id, name, state}
  const [transitionKey, setTransitionKey] = useState(0); // bumped on scene change to re-trigger CSS transition
  const [nowPlaying, setNowPlaying] = useState({ track: null, musicPlaying: false, musicVol: 0.5, announcer: "off" });
  const [recording, setRecording] = useState(null); // { mode: "local"|"cloud", recorder, chunks, startedAt }
  const [cameraSettings, setCameraSettings] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(`tapin_cam_settings:${streamId}`) || "null");
      return { ...DEFAULT_CAM_SETTINGS, ...(saved || {}) };
    } catch { return { ...DEFAULT_CAM_SETTINGS }; }
  });
  const persistCameraSettings = (next) => {
    setCameraSettings(next);
    try { localStorage.setItem(`tapin_cam_settings:${streamId}`, JSON.stringify(next)); } catch {}
  };
  const broadcasterRefHolder = useRef(null); // points at AdvancedDirectorPanel's broadcasterRef
  const [phoneCamStream, setPhoneCamStream] = useState(null);

  // --- Scene media sources (upload or pasted link) persisted per stream in localStorage ---
  const sceneMediaKey = (id) => `tapin_scene_media:${streamId}:${id}`;
  const loadSceneMedia = () => {
    try {
      const map = {};
      SCENES.forEach((s) => {
        const raw = localStorage.getItem(sceneMediaKey(s.id));
        if (raw) map[s.id] = JSON.parse(raw);
      });
      return map;
    } catch { return {}; }
  };
  const [sceneMedia, setSceneMedia] = useState(() => loadSceneMedia());
  const [mediaPicker, setMediaPicker] = useState(null); // { target: "scene:intro", initial }
  const saveSceneMedia = (id, data) => {
    setSceneMedia((m) => {
      const next = { ...m };
      if (data) next[id] = data; else delete next[id];
      return next;
    });
    if (data) localStorage.setItem(sceneMediaKey(id), JSON.stringify(data));
    else localStorage.removeItem(sceneMediaKey(id));
  };
  const [chat, setChat] = useState([]);
  const [chatMsg, setChatMsg] = useState("");
  const [viewerCount, setViewerCount] = useState(0);
  const [commentsEnabled, setCommentsEnabled] = useState(true);
  const chatScrollRef = useRef(null);
  const savingComments = useRef(false);

  // --- Load stream + keep viewer count fresh ---
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await api.get(`/live/${streamId}`);
        if (!mounted) return;
        setStream(data);
        setViewerCount(data.viewer_count || 0);
        if (typeof data.comments_enabled === "boolean") setCommentsEnabled(data.comments_enabled);
      } catch (e) {
        toast.error(formatApiError(e));
        navigate("/studio/live");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [streamId, navigate]);

  // Load soundboard FX so the left rail can show quick-fire buttons
  useEffect(() => {
    api.get("/soundboard/presets").then(({ data }) => setSoundboardPresets(data || [])).catch(() => {});
  }, []);

  // Poll the broadcaster's music/announcer state so the left-rail Now Playing
  // strip (and the rack's announcer pads) stay reactive.
  useEffect(() => {
    const h = setInterval(() => {
      const s = broadcasterRefHolder.current?.getState?.();
      if (s) setNowPlaying(s);
    }, 400);
    return () => clearInterval(h);
  }, []);

  // Pull guest list (waiting room) every 5s so mixer can add per-guest channels
  const refreshGuests = useCallback(async () => {
    try {
      const { data } = await api.get(`/live/${streamId}/guests`);
      setGuests(data || []);
    } catch {}
  }, [streamId]);
  useEffect(() => {
    if (!stream) return;
    let stopped = false;
    const pull = async () => {
      if (stopped) return;
      await refreshGuests();
    };
    pull();
    const h = setInterval(pull, 5000);
    return () => { stopped = true; clearInterval(h); };
  }, [stream, refreshGuests]);

  const fireSfx = (clip) => {
    try {
      const a = new Audio(clip.url);
      a.crossOrigin = "anonymous";
      a.volume = 0.9;
      a.play().catch(() => {});
      toast.success(`${clip.emoji || "🔊"} ${clip.label}`, { id: "sfx-fire" });
    } catch {}
  };

  // Corner overlay launchers — push overlay state via the broadcaster WS so
  // viewers see the lower-third / weather / BRB / ticker INSTANTLY without
  // opening the Pro Kit. Falls back to REST scene switch when WS isn't ready.
  const fireOverlay = (key) => {
    const bRef = broadcasterRefHolder.current?.current;
    if (!bRef?.sendOverlays) {
      toast.info("Go live first — overlays activate once the WS signaling is up.", { id: "overlay-fire" });
      return;
    }
    const payload = {
      lower_third: key === "lower_third" ? { visible: true, name: stream.creator_name || "Host", title: stream.category || "Live" } : undefined,
      ticker: key === "ticker" ? { visible: true, text: "LIVE NOW · TAP IN TV" } : undefined,
      brb: key === "brb" ? { visible: true, label: "Be Right Back" } : undefined,
      weather: key === "weather" ? { visible: true, city: "Los Angeles" } : undefined,
      clear: key === "clear" ? true : undefined,
    };
    bRef.sendOverlays(payload);
    toast.success(key === "clear" ? "Overlays cleared" : `Overlay: ${key}`, { id: "overlay-fire" });
  };

  // Poll viewer count every 4s
  useEffect(() => {
    if (!stream) return;
    const h = setInterval(async () => {
      try {
        const { data } = await api.get(`/live/${streamId}`);
        setViewerCount(data.viewer_count || 0);
      } catch {}
    }, 4000);
    return () => clearInterval(h);
  }, [stream, streamId]);

  // Poll chat only while drawer is open (save bandwidth when hidden)
  useEffect(() => {
    if (!chatOpen || !stream) return;
    let stopped = false;
    const pull = async () => {
      try {
        const { data } = await api.get(`/live/${streamId}/chat`);
        if (stopped) return;
        setChat(data || []);
        requestAnimationFrame(() => {
          if (chatScrollRef.current) chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
        });
      } catch {}
    };
    pull();
    const h = setInterval(pull, 4000);
    return () => { stopped = true; clearInterval(h); };
  }, [chatOpen, stream, streamId]);

  const sendChat = async (e) => {
    e?.preventDefault();
    const msg = (chatMsg || "").trim();
    if (!msg) return;
    try {
      await api.post(`/live/${streamId}/chat`, { message: msg });
      setChatMsg("");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const toggleComments = async () => {
    if (!stream || savingComments.current) return;
    savingComments.current = true;
    const next = !commentsEnabled;
    setCommentsEnabled(next);
    try {
      await api.put(`/live/${streamId}`, { comments_enabled: next });
      toast.success(next ? "Viewer comments ON" : "Viewer comments OFF", { id: "comments-toggle" });
    } catch (e) {
      setCommentsEnabled(!next);
      toast.error("Couldn't update comments setting", { id: "comments-toggle" });
    } finally {
      savingComments.current = false;
    }
  };

  const switchScene = async (scene) => {
    if (!stream) return;
    try {
      await api.post(`/live/${streamId}/scene`, { scene });
      setStream((s) => ({ ...s, scene }));
      setTransitionKey((k) => k + 1);
      toast.success(`Scene: ${scene}`, { id: "scene-switch" });
    } catch (e) {
      toast.error("Couldn't switch scene", { id: "scene-switch" });
    }
  };

  const startRecording = async (mode /* "local" | "cloud" | "live_cloud" */) => {
    if (recording) return;
    if (!user || !(user.role === "admin" || user.role === "premium_creator" || user.advanced_director_granted)) {
      toast.error("Recording is a Premium Creator feature. Upgrade to enable.", { id: "rec-gate" });
      return;
    }
    const proxy = broadcasterRefHolder.current;
    // Renamed from `stream` → `mediaStream` to avoid shadowing the page-
    // scope `stream` state (the live stream record from /api/live/...).
    // The MediaStream we capture here is the broadcaster's local
    // composited camera+mic feed.
    const mediaStream = proxy?.current?.streamRef?.current;
    if (!mediaStream) { toast.info("Start the broadcast first — recording captures the live stream.", { id: "rec-start" }); return; }
    try {
      const recorder = new MediaRecorder(mediaStream, { mimeType: "video/webm;codecs=vp9,opus" });
      const chunks = [];
      let seq = 0;
      let recId = null;

      if (mode === "live_cloud") {
        try {
          const { data } = await api.post("/recordings/start", { stream_id: streamId });
          recId = data.id;
        } catch (e) {
          toast.error("Couldn't start live cloud recording — try HD or HD+Cloud instead.");
          return;
        }
      }

      recorder.ondataavailable = async (e) => {
        if (!e.data || e.data.size === 0) return;
        chunks.push(e.data);
        if (mode === "live_cloud" && recId) {
          try {
            const fd = new FormData();
            fd.append("file", e.data, `chunk-${seq}.webm`);
            await api.post(`/recordings/${recId}/chunk?seq=${seq}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            seq += 1;
          } catch {}
        }
      };
      recorder.onstop = async () => {
        const blob = new Blob(chunks, { type: "video/webm" });
        // Hard drive modes: trigger download
        if (mode === "local" || mode === "cloud") {
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = `tapin-${streamId}-${Date.now()}.webm`;
          document.body.appendChild(a); a.click(); a.remove();
          setTimeout(() => URL.revokeObjectURL(url), 5000);
          toast.success("Recording saved to your downloads folder", { id: "rec-done" });
        }
        if (mode === "cloud") {
          try {
            const fd = new FormData();
            fd.append("file", blob, `tapin-${streamId}-${Date.now()}.webm`);
            fd.append("stream_id", streamId);
            await api.post("/recordings/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success("Recording uploaded to your Tap IN library", { id: "rec-cloud" });
          } catch {
            toast.error("Cloud upload failed — local copy is safe in Downloads.", { id: "rec-cloud" });
          }
        }
        if (mode === "live_cloud" && recId) {
          try {
            await api.post(`/recordings/${recId}/finalize`);
            toast.success("Live cloud recording finalized — available in your library", { id: "rec-live" });
          } catch {
            toast.error("Couldn't finalize — chunks are saved; contact support to stitch.", { id: "rec-live" });
          }
        }
      };
      recorder.start(mode === "live_cloud" ? 2000 : 1000); // 2s chunks for live cloud
      setRecording({ mode, recorder, chunks, startedAt: Date.now(), recId });
      const label = mode === "live_cloud" ? "Live cloud recording (chunked)" : mode === "cloud" ? "Recording to hard drive + cloud" : "Recording to hard drive";
      toast.success(label, { id: "rec-start" });
    } catch (e) {
      toast.error("Your browser can't record this stream");
    }
  };

  const stopRecording = () => {
    if (!recording) return;
    try { recording.recorder.stop(); } catch {}
    setRecording(null);
  };

  const copyInvite = () => {
    if (!stream?.invite_code) return;
    navigator.clipboard.writeText(stream.invite_code);
    toast.success("Invite code copied");
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-[#05070C] flex items-center justify-center z-50">
        <div className="animate-spin w-10 h-10 border-2 border-tv-accent border-t-transparent rounded-full" />
      </div>
    );
  }
  if (!stream) return null;

  const SCENE_ACTIVE = stream.scene;

  return (
    <div
      data-testid="director-console"
      className="fixed inset-0 z-40 bg-[#05070C] text-white flex flex-col overflow-hidden"
      style={{ backgroundImage: "radial-gradient(1200px 600px at 50% -200px, rgba(255,212,0,0.06), transparent)" }}
    >
      {/* ---- TOP TOOLBAR ----
          All the toolbar markup moved into ConsoleTopToolbar so this
          page stays readable. Pure presentation; the broadcast ref
          proxy (broadcasterRefHolder) is reached via callbacks. */}
      <ConsoleTopToolbar
        stream={stream}
        sceneActive={stream.scene}
        bcStatus={bcStatus}
        viewerCount={viewerCount}
        recording={recording}
        commentsEnabled={commentsEnabled}
        chatOpen={chatOpen}
        onExit={() => navigate("/studio/live")}
        onStartBroadcast={() => broadcasterRefHolder.current?.current?.startBroadcast?.()}
        onEndBroadcast={() => broadcasterRefHolder.current?.current?.endBroadcast?.()}
        onCopyInvite={copyInvite}
        onToggleComments={toggleComments}
        onOpenFx={() => setFxOpen(true)}
        onStartRecording={startRecording}
        onStopRecording={stopRecording}
        onToggleChat={() => setChatOpen((v) => !v)}
      />

      {/* ---- MAIN WORKSPACE: upper row (left rail · center preview · monitors) + bottom mixer + overlay chat drawer ---- */}
      {/* On mobile (< md) the 3-column strict layout collapses into a single
          scrollable column with a bottom tab bar that swaps which panel is
          in focus. This keeps the same functionality reachable on phones. */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="flex-1 flex flex-col md:flex-row overflow-y-auto md:overflow-hidden">
          {/* LEFT — Sources / Scenes / Overlays / Quick Soundboard */}
          <aside
            data-testid="console-left"
            className={`${["scenes", "guests"].includes(mobilePanel) ? "flex" : "hidden"} md:flex flex-col shrink-0 w-full md:w-[240px] md:h-auto border-b md:border-b-0 md:border-r border-white/10 bg-[#080B12] p-3 md:overflow-y-auto`}
          >
          {/* Crew Boost launcher + Scenes grid — extracted to
              ConsoleScenesRail for readability. */}
          <ConsoleScenesRail
            streamId={streamId}
            scenes={SCENES}
            activeId={SCENE_ACTIVE}
            sceneMedia={sceneMedia}
            onSwitch={switchScene}
            onEditMedia={setMediaPicker}
          />

          {/* Guests — invite + accept/reject/block pending requests + assign cams. Collapsible. */}
          <GuestManager
            streamId={streamId}
            inviteCode={stream.invite_code}
            guests={guests}
            onRefresh={refreshGuests}
          />

          {/* QR overlay — pin a payment / call-to-action QR on the
              broadcast frame. Same panel DirectorsMode surfaces in its
              Overlays tab. Rendered above the preview via QrOverlayLayer. */}
          <div className="mt-5 border-t border-white/10 pt-3" data-testid="console-qr-overlay-wrap">
            <QrOverlayPanel
              streamId={streamId}
              currentOverlays={stream.overlays}
            />
          </div>

          {/* Transitions — persisted in fxSettings, same presets as Studio FX modal */}
          <div className="mt-5 border-t border-white/10 pt-3">
            <div className="text-[10px] uppercase tracking-wider font-bold text-white/50 mb-2 flex items-center gap-1"><Zap className="w-3 h-3 text-tv-accent" /> Transitions</div>
            <div className="grid grid-cols-3 gap-1">
              {[
                { k: "cut", l: "Cut" },
                { k: "fade", l: "Fade" },
                { k: "dip_black", l: "Dip" },
                { k: "slide_left", l: "Slide" },
                { k: "slide_up", l: "Push" },
              ].map((t) => {
                const active = (fxSettings?.transition?.mode || "fade") === t.k;
                return (
                  <button
                    key={t.k}
                    data-testid={`left-transition-${t.k}`}
                    onClick={() => {
                      const next = { ...(fxSettings || {}), transition: { ...(fxSettings?.transition || { duration_ms: 300 }), mode: t.k } };
                      setFxSettings(next);
                      try { localStorage.setItem("tapin_studio_fx", JSON.stringify(next)); } catch {}
                      toast.success(`Transition: ${t.l}`, { id: "trans-pick" });
                    }}
                    aria-pressed={active}
                    className={`text-[10px] font-bold py-1.5 rounded border transition ${active ? "bg-tv-accent text-black border-tv-accent" : "bg-white/5 text-white/70 border-white/10 hover:bg-white/10"}`}
                  >
                    {t.l}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Quick Overlays — one-click launchers that live below the preview */}
          <div className="mt-5 border-t border-white/10 pt-3">
            <div className="text-[10px] uppercase tracking-wider font-bold text-white/50 mb-2 flex items-center gap-1"><Film className="w-3 h-3 text-tv-accent" /> Overlays</div>
            <div className="grid grid-cols-2 gap-1.5">
              {[
                { id: "weather", label: "Weather", Icon: Cloud },
                { id: "brb", label: "BRB", Icon: Coffee },
                { id: "pip_url", label: "PIP Link", Icon: Link2 },
                { id: "pip_cam", label: "PIP Cam", Icon: PictureInPicture2 },
              ].map(({ id, label, Icon }) => (
                <button
                  key={id}
                  data-testid={`console-overlay-${id}`}
                  onClick={() => {
                    setProKitOpen(true);
                    toast.info(`Open ${label} in Pro Kit below`, { id: "pro-kit" });
                    requestAnimationFrame(() => document.getElementById("pro-kit-tray")?.scrollIntoView({ behavior: "smooth", block: "start" }));
                  }}
                  className="flex items-center gap-1.5 bg-white/5 hover:bg-white/10 text-white text-[10px] font-bold px-2 py-1.5 rounded"
                >
                  <Icon className="w-3 h-3 text-tv-accent shrink-0" /> {label}
                </button>
              ))}
            </div>
          </div>

          {/* Quick SFX — first 8 presets so the host can hit stingers without opening the Pro Kit */}
          {soundboardPresets.length > 0 && (
            <div className="mt-5 border-t border-white/10 pt-3">
              <div className="text-[10px] uppercase tracking-wider font-bold text-white/50 mb-2 flex items-center gap-1"><Zap className="w-3 h-3 text-tv-accent" /> Quick SFX</div>
              <div className="grid grid-cols-4 gap-1">
                {soundboardPresets.slice(0, 8).map((clip) => (
                  <button
                    key={clip.id}
                    data-testid={`console-sfx-${clip.id}`}
                    onClick={() => fireSfx(clip)}
                    title={clip.label}
                    className="aspect-square rounded-md bg-white/5 hover:bg-tv-accent/25 hover:text-tv-accent text-white text-base flex items-center justify-center"
                  >
                    {clip.emoji || "🔊"}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="mt-5 border-t border-white/10 pt-3">
            {/* --- NOW PLAYING (moved here from mixer — per user layout sketch) --- */}
            <div data-testid="left-now-playing" className="mb-3 bg-black/40 border border-violet-400/20 rounded-lg p-2">
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-black text-violet-300 mb-1.5">
                <Music2 className="w-3 h-3" /> Now Playing
              </div>
              <div className="text-[11px] text-white font-bold truncate" title={nowPlaying.track?.title}>
                {nowPlaying.track?.title || <span className="text-white/40 italic">No track</span>}
              </div>
              {nowPlaying.track?.artist && (
                <div className="text-[10px] text-white/50 truncate mb-1.5">{nowPlaying.track.artist}</div>
              )}
              <div className="flex gap-1.5 mt-1.5">
                <button
                  data-testid="left-music-play"
                  onClick={() => broadcasterRefHolder.current?.toggleMusicPlay?.()}
                  className={`flex-1 flex items-center justify-center gap-1 text-[10px] font-black rounded-md py-1.5 ${nowPlaying.musicPlaying ? "bg-violet-500 text-white" : "bg-white/5 hover:bg-white/10 text-white"}`}
                >
                  {nowPlaying.musicPlaying ? "❙❙ Pause" : "▶ Play"}
                </button>
                <button
                  data-testid="left-music-pick"
                  onClick={() => broadcasterRefHolder.current?.openMusicPicker?.()}
                  className="flex-1 bg-white/5 hover:bg-white/10 text-white text-[10px] font-black py-1.5 rounded-md"
                >
                  Library
                </button>
              </div>
              <input
                type="range"
                min="0" max="1.2" step="0.02"
                value={nowPlaying.musicVol ?? 0.5}
                onChange={(e) => broadcasterRefHolder.current?.setMusicVol?.(parseFloat(e.target.value))}
                className="w-full accent-violet-400 mt-1.5"
                data-testid="left-music-vol"
              />
            </div>

            <button
              onClick={() => window.open(`/watch/live/${stream.id}`, "_blank")}
              data-testid="console-open-viewer"
              className="w-full text-left text-xs bg-white/5 hover:bg-white/10 px-3 py-2 rounded-lg flex items-center gap-2"
            >
              <Maximize2 className="w-3 h-3 text-tv-accent" /> Open viewer page
            </button>
          </div>
        </aside>

        {/* CENTER — smaller framed preview + in-column bottom mixer */}
        <section className={`${mobilePanel === "preview" || mobilePanel === "mixer" ? "flex" : "hidden"} md:flex flex-1 md:overflow-hidden flex-col`}>
          {/* Above-stream overlay strip — quick triggers for newsroom overlays */}
          <div className={`${mobilePanel === "preview" ? "flex" : "hidden md:flex"} items-center gap-2 px-4 pt-3 flex-wrap justify-center shrink-0`}>
            {[
              { key: "lower_third", label: "Lower Third", Icon: Film },
              { key: "ticker", label: "Ticker", Icon: Zap },
              { key: "weather", label: "Weather", Icon: Cloud },
              { key: "brb", label: "BRB", Icon: Coffee },
              { key: "clear", label: "Clear", Icon: X },
            ].map(({ key, label, Icon }) => (
              <button
                key={key}
                data-testid={`console-overlay-top-${key}`}
                onClick={() => fireOverlay(key)}
                className={`flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-lg border transition ${
                  key === "clear"
                    ? "bg-red-500/10 text-red-200 border-red-500/40 hover:bg-red-500/20"
                    : "bg-white/5 text-white/80 border-white/10 hover:bg-white/10 hover:text-tv-accent"
                }`}
              >
                <Icon className="w-3 h-3" /> {label}
              </button>
            ))}
          </div>

          <div className={`${mobilePanel === "preview" ? "flex" : "hidden md:flex"} p-3 flex-1 flex-col items-center justify-start overflow-hidden min-h-0`}>
            <div className="relative w-full max-w-[900px] mx-auto">
              {/* Live-only share + AI thumbnail strip — matches the
                  DirectorsMode surface so hosts get the same
                  one-tap social reach + auto-thumbnail capture inside
                  Full Studio. */}
              {stream?.is_live && (
                <div
                  data-testid="console-live-share-strip"
                  className="mb-3 space-y-2"
                >
                  <GoLiveShareBar streamId={stream.id} title={stream.title} />
                  <AiThumbnailPicker
                    streamId={stream.id}
                    currentThumbnail={stream.thumbnail_url}
                    autoIntervalSec={120}
                    onChosen={(url) => setStream((s) => (s ? { ...s, thumbnail_url: url } : s))}
                  />
                </div>
              )}
              <div
                data-testid="console-preview-frame"
                key={`preview-${transitionKey}`}
                className={`relative w-full rounded-2xl border border-tv-accent/20 shadow-[0_0_32px_rgba(255,212,0,0.08)] overflow-hidden bg-black ${
                  fxSettings?.transition?.mode ? `scene-transition-${fxSettings.transition.mode}` : "scene-transition-fade"
                }`}
                style={{ "--scene-transition-ms": `${fxSettings?.transition?.duration_ms || 300}ms` }}
              >
                <AdvancedDirectorPanel
                  streamId={stream.id}
                  kidsBrand={stream.category === "kids"}
                  embedded={true}
                  fxSettings={fxSettings}
                  cameraSettings={cameraSettings}
                  phoneCamStream={phoneCamStream}
                  onEnd={() => navigate("/studio/live")}
                  onStatusChange={setBcStatus}
                  onAudioReady={setAudioBus}
                  onBroadcasterReady={(ref) => { broadcasterRefHolder.current = ref; }}
                />
                {/* QR overlay preview — rendered on top so the host sees
                    exactly where the QR will land for viewers. The live
                    Watch.jsx player uses the same layer. */}
                <QrOverlayLayer overlays={stream.overlays} />
              </div>
            </div>
          </div>

          {/* Mixer stationed at the bottom of the CENTER column. Collapsible
              drawer — tap the handle to expand/collapse on any screen size.
              On mobile, visible full-height when the Mixer tab is selected. */}
          <div className={`${mobilePanel === "mixer" ? "block flex-1 overflow-y-auto" : "hidden md:block"} shrink-0 border-t border-tv-accent/30`}>
            <button
              data-testid="mixer-drawer-toggle"
              onClick={() => setMixerOpen((v) => !v)}
              className="w-full flex items-center justify-center gap-2 py-1 bg-black/70 hover:bg-black text-white/70 hover:text-tv-accent text-[10px] uppercase tracking-wider font-black border-b border-white/10"
              title="Toggle mixer drawer"
            >
              <Sliders className="w-3 h-3" /> Mixer {mixerOpen ? "▾" : "▴"}
            </button>
            {mixerOpen && (
              <DirectorMixerRack
                open={true}
                onToggle={() => {}}
                audioCtx={audioBus.audioCtx}
                outputDest={audioBus.outputDest}
                guests={guests}
                floating={false}
                onToggleFloat={() => {}}
                broadcasterProxy={broadcasterRefHolder.current}
              />
            )}
          </div>
        </section>

        {/* MONITORS column — camera picker, stage layouts, waiting room, ad preview */}
        <aside data-testid="console-monitors" className={`${mobilePanel === "overlays" ? "flex" : "hidden"} xl:flex shrink-0 w-full xl:w-[260px] flex-col gap-2 border-t md:border-t-0 xl:border-l border-white/10 bg-[#080B12] p-3 md:overflow-y-auto console-scroll`}>
          <div className="flex items-center gap-2 text-white/60 font-display font-bold text-xs uppercase tracking-wider mb-1">
            <PictureInPicture2 className="w-4 h-4 text-tv-accent" /> Monitors
          </div>

          {/* Camera picker — enumerates devices via navigator.mediaDevices */}
          <CameraPicker
            onSelect={(deviceId, label) => {
              const proxy = broadcasterRefHolder.current;
              if (proxy?.setProgramCamera) { proxy.setProgramCamera(deviceId); toast.success(`Camera → ${label}`, { id: "cam-pick" }); }
              else toast.info("Start the broadcast first to switch cameras", { id: "cam-pick" });
            }}
          />

          {/* Camera Settings — rotation, mirror, flashlight, HD, FPS, color adjust */}
          <CameraSettingsPanel
            settings={cameraSettings}
            onChange={persistCameraSettings}
            mediaStream={broadcasterRefHolder.current?.current?.media?.stream || null}
            onFlip={() => {
              const bc = broadcasterRefHolder.current?.current;
              if (bc?.flipCamera) bc.flipCamera();
              else toast.info("Start the camera first", { id: "cam-flip" });
            }}
            onReacquire={async ({ width, height, fps }) => {
              const bc = broadcasterRefHolder.current?.current;
              if (!bc?.reacquireCamera || !bc.media?.stream) {
                toast.info("Start the camera first", { id: "cam-reacq" });
                return;
              }
              const ok = await bc.reacquireCamera({ width, height, fps });
              if (ok) toast.success(`Camera → ${width}×${height} @ ${fps}fps`, { id: "cam-reacq" });
              else toast.error("Couldn't re-acquire camera", { id: "cam-reacq" });
            }}
            defaultOpen={false}
          />

          {/* Phone-as-camera pairing — extra camera source via QR-paired phone */}
          <PhoneCameraPanel
            onStream={(stream) => {
              setPhoneCamStream(stream);
              window.__tapin_phone_cam_stream = stream;
              toast.success("Phone camera connected — choose it from the PIP selector or 'Take phone FULL'");
            }}
            onEnded={() => { setPhoneCamStream(null); window.__tapin_phone_cam_stream = null; }}
          />

          {/* Multistream to social platforms */}
          <RestreamPanel
            getBroadcastStream={() => broadcasterRefHolder.current?.current?.media?.stream || null}
            meta={{
              show_name: stream?.title || "Live broadcast",
              host_name: user?.display_name || user?.username,
              station: stream?.title || "",
              listen_url: stream ? `${window.location.origin}/watch/${stream.id}` : "",
            }}
            title="Multistream to socials"
          />
          <AnnouncementsPanel defaultOpen meta={{
            show_name: stream?.title || "Live broadcast",
            host_name: user?.display_name || user?.username,
            station: stream?.title || "",
            listen_url: stream ? `${window.location.origin}/watch/${stream.id}` : "",
          }} />

          {/* Stage layout — see ConsoleStagePanel for the chip grid + guest-hint anchor.
              The same layout state is shared bidirectionally with
              AdvancedDirectorPanel via localStorage + a same-tab
              CustomEvent (tapin:stage-layout). */}
          <ConsoleStagePanel />

          {/* Ad Schedule — up to 20 ad breaks with per-slot media (upload/link) + cue time offsets */}
          <AdSchedule
            streamId={streamId}
            bcStatus={bcStatus}
            startedAt={stream.started_at}
            defaultOpen={false}
          />

          {/* --- NEWSROOM CONTROLS — collapsible, sends lower-third / breaking / ticker to viewers --- */}
          <NewsroomControls broadcasterProxy={broadcasterRefHolder.current} defaultOpen={false} />

          {/* 3 camera preview thumbnails (per user layout) */}
          <div className="text-[9px] uppercase tracking-wider font-black text-white/50 mt-1">Camera Previews</div>
          <MonitorTile label="Camera 1" badge="CAM" badgeColor="bg-emerald-500 text-white" />
          <MonitorTile label="Camera 2" badge="CAM" badgeColor="bg-emerald-500 text-white" />
          <MonitorTile label="Camera 3" badge="CAM" badgeColor="bg-emerald-500 text-white" />

          {/* --- BACK STAGE — guest waiting room, now collapsible and placed ABOVE Announcer FX --- */}
          <BackstagePanel guests={guests} />

          {/* --- ANNOUNCER FX — moved here from the mixer rack --- */}
          <ConsoleAnnouncerFx
            activeKey={nowPlaying.announcer}
            onSelect={(k) => broadcasterRefHolder.current?.setAnnouncer?.(k)}
          />
        </aside>

        {/* FAR-RIGHT — Chat drawer (tucked away by default; "hide comments neatly" request) */}
        <aside
          data-testid="console-chat-drawer"
          className={`shrink-0 border-l border-white/10 bg-[#080B12] transition-all overflow-hidden flex flex-col ${
            chatOpen ? "w-[340px]" : "w-0"
          }`}
        >
          {chatOpen && (
            <>
              <div className="px-4 py-3 border-b border-white/10 flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-tv-accent" />
                <div className="font-display font-bold text-sm">Live Chat</div>
                <div className="flex-1" />
                <div className="text-[10px] uppercase tracking-wider text-white/40">{chat.length} msgs</div>
              </div>

              <div ref={chatScrollRef} data-testid="console-chat-list" className="flex-1 overflow-y-auto p-4 space-y-2 text-sm">
                {chat.length === 0 && <div className="text-white/40 text-xs italic">No messages yet. Once viewers join, their comments appear here.</div>}
                {chat.map((m) => (
                  <div key={m.id} data-testid={`console-chat-msg-${m.id}`} className="flex items-start gap-2">
                    <div className="w-6 h-6 rounded-full bg-tv-brand text-black font-black text-xs flex items-center justify-center shrink-0">
                      {m.user_name?.[0] || "?"}
                    </div>
                    <div className="min-w-0">
                      <span className="text-tv-brand font-semibold text-[11px]">{m.user_name}</span>{" "}
                      <span className="text-white/80 break-words">{m.message}</span>
                    </div>
                  </div>
                ))}
              </div>

              <form onSubmit={sendChat} className="p-3 border-t border-white/10 flex gap-2">
                <input
                  data-testid="console-chat-input"
                  value={chatMsg}
                  onChange={(e) => setChatMsg(e.target.value)}
                  placeholder={commentsEnabled ? "Say something…" : "Comments disabled — only you can post"}
                  className="flex-1 bg-white/5 border border-white/10 rounded-full px-3 py-1.5 text-xs text-white outline-none focus:border-tv-accent"
                />
                <button
                  type="submit"
                  data-testid="console-chat-send"
                  className="bg-tv-accent hover:bg-yellow-400 text-black font-bold w-9 h-9 rounded-full flex items-center justify-center"
                  aria-label="Send"
                >
                  <Send className="w-4 h-4" />
                </button>
              </form>
            </>
          )}
        </aside>
        </div>

        {/* MOBILE TAB BAR — visible below md only. Lets the host flip between
            Scenes · Preview · Mixer · Guests · Overlays with big thumb-friendly buttons. */}
        <div
          data-testid="console-mobile-tabs"
          className="md:hidden shrink-0 flex items-center justify-around gap-0.5 bg-black border-t border-white/10 px-1 py-1.5"
        >
          {[
            { k: "scenes",   label: "Scenes",   Icon: Layers },
            { k: "preview",  label: "Preview",  Icon: RadioIcon },
            { k: "mixer",    label: "Mixer",    Icon: Sliders },
            { k: "guests",   label: "Guests",   Icon: Users },
            { k: "overlays", label: "Overlays", Icon: Wand2 },
          ].map(({ k, label, Icon }) => {
            const active = mobilePanel === k;
            return (
              <button
                key={k}
                data-testid={`console-mobile-tab-${k}`}
                onClick={() => {
                  setMobilePanel(k);
                  // Scroll to GuestManager section when Guests tab is selected
                  if (k === "guests") {
                    requestAnimationFrame(() => {
                      document.querySelector('[data-testid="left-guest-manager"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
                    });
                  }
                }}
                className={`flex-1 flex flex-col items-center gap-0.5 py-1.5 rounded-lg text-[9px] font-black uppercase tracking-wider ${
                  active ? "bg-tv-accent text-black" : "text-white/60 hover:text-white"
                }`}
              >
                <Icon className="w-4 h-4" />
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Scene transition animations + studio fx modal */}
      <style>{`
        .console-scroll::-webkit-scrollbar { width: 8px; }
        .console-scroll::-webkit-scrollbar-track { background: transparent; }
        .console-scroll::-webkit-scrollbar-thumb {
          background: rgba(255, 212, 0, 0.25);
          border-radius: 4px;
          border: 1px solid rgba(255, 212, 0, 0.15);
        }
        .console-scroll::-webkit-scrollbar-thumb:hover { background: rgba(255, 212, 0, 0.5); }
        .console-scroll { scrollbar-width: thin; scrollbar-color: rgba(255, 212, 0, 0.3) transparent; }
        .scene-transition-fade    { animation: tv-scene-fade var(--scene-transition-ms, 300ms) ease both; }
        .scene-transition-cut     { animation: none; }
        .scene-transition-dip_black { animation: tv-scene-dip var(--scene-transition-ms, 300ms) ease both; }
        .scene-transition-slide_left { animation: tv-scene-slide-left var(--scene-transition-ms, 300ms) cubic-bezier(.22,.72,.26,1) both; }
        .scene-transition-slide_up   { animation: tv-scene-slide-up   var(--scene-transition-ms, 300ms) cubic-bezier(.22,.72,.26,1) both; }
        @keyframes tv-scene-fade { from { opacity: 0; } to { opacity: 1; } }
        @keyframes tv-scene-dip  { 0% { opacity: 0; filter: brightness(0.05); } 50% { opacity: 0.2; filter: brightness(0.1); } 100% { opacity: 1; filter: none; } }
        @keyframes tv-scene-slide-left { from { transform: translateX(40px); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
        @keyframes tv-scene-slide-up   { from { transform: translateY(30px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
      `}</style>

      <StudioFxModal
        open={fxOpen}
        onClose={() => setFxOpen(false)}
        onApply={(s) => setFxSettings(s)}
        initial={fxSettings}
        streamId={streamId}
      />

      {/* Media source picker — opened from Scene tiles OR the Ad preview slot */}
      <MediaSourcePicker
        open={!!mediaPicker}
        onClose={() => setMediaPicker(null)}
        title={mediaPicker?.label ? `${mediaPicker.label} media` : "Scene media"}
        initial={mediaPicker?.initial || null}
        onSave={(data) => {
          if (!mediaPicker) return;
          if (mediaPicker.target?.startsWith("scene:")) {
            const id = mediaPicker.target.slice("scene:".length);
            saveSceneMedia(id, data);
          }
        }}
        onClear={() => {
          if (!mediaPicker) return;
          if (mediaPicker.target?.startsWith("scene:")) {
            const id = mediaPicker.target.slice("scene:".length);
            saveSceneMedia(id, null);
          }
        }}
      />
    </div>
  );
}

